import { useCallback, useEffect, useMemo } from 'react'

import { FormattedMessage } from 'react-intl'

import type { Budget } from '@subaio/api-types/budget'
import type { UserBank } from '@subaio/api-types/psd2'
import { PaymentFrequency } from '@subaio/api-types/shared'
import { useAsyncInterval, useMounted, useStepHandlers } from '@subaio/components'
import { getCountry, logError } from '@subaio/utils'

import type { Asset } from 'apiTypes/Asset'
import type { BudgetMetadata } from 'apiTypes/BudgetMetadata'

import { asContainer } from 'lib/container'
import { assertAuthenticated } from 'lib/userAuth'

import { StepWizard } from 'components/StepWizard'

import { type Step, StepType } from './Step'
import {
	Accounts,
	Assets,
	BudgetEditForm,
	BudgetPoll,
	Introduction,
	MissingOtherApplicant,
	Redirect,
	Success,
	Terms,
} from './Steps'
import { ApplicantType, getStepHandlers } from './stepHandler'
import type { UserMetadata } from './UserMetadata'

type State = {
	step?: Step
	userMetadata: UserMetadata
}

export const container = asContainer<State>()({
	authentication: { type: 'private' },
	// No path, since this cannot be routed to through URL
	dependencies: {
		userMetadata: ({ userAuth }) => {
			assertAuthenticated(userAuth)
			return {
				path: `/budgets/${userAuth.user.budgetId}/metadata`,
				transform: (metadata: BudgetMetadata) =>
					metadata.coapplicant.name
						? {
								userName: metadata.isPrimary ? metadata.applicant!.name : metadata.coapplicant.name,
								otherApplicantName: metadata.isPrimary ? metadata.coapplicant.name : metadata.applicant!.name,
								advisorName: metadata.advisor!.name,
								applicantType: ApplicantType.MultipleApplicants,
								assets: metadata.additionalInformation?.assets ?? [],
							}
						: {
								userName: metadata.applicant!.name,
								otherApplicantName: undefined,
								advisorName: metadata.advisor!.name,
								applicantType: ApplicantType.SingleApplicant,
								assets: metadata.additionalInformation?.assets ?? [],
							},
			}
		},
	},
	initialize: async ({ userAuth, api }) => {
		assertAuthenticated(userAuth)

		return {
			step: await getStepHandlers({
				userId: userAuth.user.userId,
				budgetId: userAuth.user.budgetId,
				sessionId: userAuth.user.sessionId,
				brandSteps: userAuth.user.brandSteps,
				redirectUrl: userAuth.user.redirectUrl,
				api,
				onLogEvent: () => undefined,
			}).onInitial(),
		}
	},
	Running: ({ step, userMetadata, setData, api, userAuth, setPageTitle }) => {
		assertAuthenticated(userAuth)
		const stepHandlers = useMemo(
			() =>
				getStepHandlers({
					userId: userAuth.user.userId,
					budgetId: userAuth.user.budgetId,
					sessionId: userAuth.user.sessionId,
					brandSteps: userAuth.user.brandSteps,
					redirectUrl: userAuth.user.redirectUrl,
					api,
					onLogEvent: () => undefined,
				}),
			[
				api,
				userAuth.user.userId,
				userAuth.user.budgetId,
				userAuth.user.sessionId,
				userAuth.user.brandSteps,
				userAuth.user.redirectUrl,
			],
		)
		const { onIntroductionNext, onTermsAccept, onAccountSelectComplete, onAssetsFinish, onBudgetEditFinish } =
			useStepHandlers(
				step,
				useCallback((step) => setData({ step }), [setData]),
				stepHandlers,
			)
		const isMounted = useMounted()
		useAsyncInterval(step?.type === StepType.MissingOtherApplicant && step.isBankDataPending ? 2 : null, async () => {
			if (!isMounted()) return
			try {
				const nextStep = await stepHandlers.onOtherApplicantStatus()
				if (isMounted() && (nextStep.type !== StepType.MissingOtherApplicant || !nextStep.isBankDataPending))
					setData({ step: nextStep })
			} catch (error) {
				logError(error)
			}
		})

		const onGetBudget = useCallback(
			async () => api.get<Budget | undefined>(`/budgets/${userAuth.user.budgetId}`),
			[api, userAuth],
		)

		const getLoginUrl = useCallback(async () => {
			const { url } = await api.post<{ url: string; sessionId: string }>('/psd2/initiate', {})

			return url
		}, [api])
		const getBanks = useCallback(async () => api.get<Array<UserBank>>('/psd2/selected-banks'), [api])
		const saveAssets = useCallback(
			async (assets: Array<Asset>) => {
				await api.post<Array<Asset>>(`/budgets/${userAuth.user.budgetId}/assets`, assets)
			},
			[api, userAuth],
		)

		useEffect(() => {
			switch (step?.type) {
				case StepType.Introduction: {
					setPageTitle(
						<FormattedMessage
							id='flow.introduction.title'
							defaultMessage='Hello {userName}'
							values={{ userName: userMetadata.userName }}
						/>,
					)
					break
				}
				case StepType.Terms: {
					setPageTitle(<FormattedMessage id='flow.terms.title' defaultMessage='Consent' />)
					break
				}
				case StepType.Accounts: {
					setPageTitle(
						getCountry(userAuth.user.brandId) === 'SE' ? (
							<FormattedMessage id='flow.accounts.title.sweden' defaultMessage='Add your banks and accounts' />
						) : (
							<FormattedMessage id='flow.accounts.title' defaultMessage='Add your accounts and credit cards' />
						),
					)
					break
				}
				case StepType.Assets: {
					setPageTitle(<FormattedMessage id='flow.assets.title' defaultMessage='Add your assets' />)
					break
				}
				case StepType.BudgetEditForm: {
					setPageTitle(
						<FormattedMessage
							id='flow.budgetEditForm.title'
							defaultMessage='{applicantType, select,
							other { Review and adjust your budget}
						}'
							values={{ applicantType: userMetadata.applicantType /* Necessary for other languages than English */ }}
						/>,
					)
					break
				}
				case StepType.BudgetPoll: {
					setPageTitle(<FormattedMessage id='flow.budgetPoll.title' defaultMessage='Collecting your data' />)
					break
				}
				case StepType.MissingOtherApplicant: {
					setPageTitle(
						<FormattedMessage id='flow.missingOtherApplicant.title' defaultMessage='Waiting for co-applicant' />,
					)
					break
				}
				case StepType.Success: {
					setPageTitle(<FormattedMessage id='flow.success.title' defaultMessage='Thank you' />)
					break
				}
				case StepType.Redirect: {
					setPageTitle(<FormattedMessage id='flow.redirect.title' defaultMessage='Your data has been submitted' />)
					break
				}
			}
		}, [step, setPageTitle, userMetadata, userAuth.user])

		return (
			<StepWizard<Step>
				step={step}
				header={<div />}
				renderers={{
					[StepType.Introduction]: (currentStep) => (
						<Introduction
							{...currentStep}
							userMetadata={userMetadata}
							brandFeatures={userAuth.user.brandFeatures}
							onNext={onIntroductionNext}
						/>
					),
					[StepType.Terms]: (currentStep) => (
						<Terms {...currentStep} brandFeatures={userAuth.user.brandFeatures} onNext={onTermsAccept} />
					),
					[StepType.Accounts]: (currentStep) => (
						<Accounts
							{...currentStep}
							user={userAuth.user}
							userMetadata={userMetadata}
							brandFeatures={userAuth.user.brandFeatures}
							getLoginUrl={getLoginUrl}
							getBanks={getBanks}
							onNext={() => onAccountSelectComplete()}
						/>
					),
					[StepType.Assets]: (currentStep) => (
						<Assets
							{...currentStep}
							assets={userMetadata.assets}
							onSaveAssets={saveAssets}
							onNext={() => onAssetsFinish()}
						/>
					),
					[StepType.BudgetPoll]: (currentStep) => (
						<BudgetPoll {...currentStep} onGetBudget={onGetBudget} onBudgetReady={onBudgetEditFinish} />
					),
					[StepType.BudgetEditForm]: (currentStep) => (
						<BudgetEditForm
							{...currentStep}
							brandFeatures={userAuth.user.brandFeatures}
							userMetadata={userMetadata}
							onAddComment={(comment) => api.post(`/budgets/${userAuth.user.budgetId}/comment`, { comment })}
							onRecategorizeService={async ({ serviceId, categoryId, subCategoryId }) =>
								api.put<Budget>(`/budgets/${userAuth.user.budgetId}/services/${serviceId}/adjustments`, {
									categoryId,
									subCategoryId,
								})
							}
							onAdjustCategoryAmount={async ({ categoryId, amount }) => {
								const payload = {
									amount,
									frequency: PaymentFrequency.Monthly,
								}
								// If the amount is undefined, we assume that we want to delete the adjustment
								if (payload.amount === undefined) {
									return api.delete<Budget>(`/budgets/${userAuth.user.budgetId}/categories/${categoryId}/adjustments`)
								}
								return api.post<Budget>(
									`/budgets/${userAuth.user.budgetId}/categories/${categoryId}/adjustments`,
									payload,
								)
							}}
							onGetBudget={onGetBudget}
							onSubmitBudget={async () => onBudgetEditFinish()}
						/>
					),
					[StepType.MissingOtherApplicant]: (currentStep) => (
						<MissingOtherApplicant {...currentStep} userMetadata={userMetadata} />
					),
					[StepType.Success]: (currentStep) => <Success {...currentStep} userMetadata={userMetadata} />,
					[StepType.Redirect]: (currentStep) => <Redirect {...currentStep} />,
				}}
			/>
		)
	},
})
